import React from "react";
import { Dropdown, Tooltip } from "antd";
import { PushpinFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

export interface QuoteIdQuickNavTarget {
  /** Route to navigate to, e.g. "/otc" */
  path: string;
  /** Label shown in the dropdown menu, e.g. "OTC Snapshot" */
  label: string;
  /** Extra navigation state merged in alongside openQuoteId (e.g. openRecordId) */
  state?: Record<string, any>;
  /**
   * Pass the quote id as a `?quoteId=` query string instead of router state.
   * Use this for pages (e.g. /Quotes) that read the id from the URL rather
   * than from location.state.
   */
  useQueryParam?: boolean;
}

interface QuoteIdQuickNavProps {
  quoteId: string | undefined | null;
  targets: QuoteIdQuickNavTarget[];
  /** Inset from the right edge of the positioning parent, in px. Default 2. */
  rightOffset?: number;
}

/**
 * Single hover-reveal pin button next to a Quote ID field. Clicking it opens
 * a small menu listing the other pages that share this quote id, so there is
 * one consistent entry point instead of one icon button per destination.
 *
 * Positioning/sizing is done via inline styles (not Tailwind utility classes)
 * so it can't be clipped or misaligned by a parent's padding/box-sizing.
 */
const QuoteIdQuickNav: React.FC<QuoteIdQuickNavProps> = ({
  quoteId,
  targets,
  rightOffset = 2,
}) => {
  const navigate = useNavigate();
  const trimmed = String(quoteId || "").trim();

  if (!trimmed || targets.length === 0) return null;

  const goTo = (target: QuoteIdQuickNavTarget) => {
    if (target.useQueryParam) {
      navigate(`${target.path}?quoteId=${encodeURIComponent(trimmed)}`);
      return;
    }
    navigate(target.path, {
      state: { openQuoteId: trimmed, ...target.state },
    });
  };

  const pinStyle: React.CSSProperties = {
    position: "absolute",
    right: rightOffset,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 16,
    width: 16,
    borderRadius: "50%",
    border: "none",
    background: "linear-gradient(180deg, #2b7bf0, #004aad)",
    color: "#ffffff",
    fontSize: 9,
    lineHeight: 1,
    boxShadow: "0 1px 3px rgba(0, 74, 173, 0.45)",
    cursor: "pointer",
    padding: 0,
    zIndex: 5,
  };

  // Single target — no need for a menu, just navigate directly.
  if (targets.length === 1) {
    const target = targets[0];
    return (
      <Tooltip title={`View in ${target.label}`}>
        <button
          type="button"
          aria-label={`View in ${target.label}`}
          className="quote-id-quick-nav-pin"
          style={pinStyle}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            goTo(target);
          }}
        >
          <PushpinFilled />
        </button>
      </Tooltip>
    );
  }

  const items = targets.map((target, index) => ({
    key: String(index),
    label: `View in ${target.label}`,
  }));

  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key }) => {
          const target = targets[Number(key)];
          if (target) goTo(target);
        },
      }}
      trigger={["click"]}
      placement="bottomRight"
      overlayClassName="quote-id-quick-nav-menu"
    >
      <Tooltip title="View this quote elsewhere">
        <button
          type="button"
          aria-label="View this quote elsewhere"
          className="quote-id-quick-nav-pin"
          style={pinStyle}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <PushpinFilled />
        </button>
      </Tooltip>
    </Dropdown>
  );
};

export default QuoteIdQuickNav;
